<template>
  <section v-if="drafts.length" class="agent-reply-draft" aria-label="AI suggested reply">
    <template v-for="action in drafts" :key="action.id">
      <!-- Collapsed by default: one line, so the conversation above stays readable. -->
      <button
        v-if="!expandedIds.has(action.id)"
        type="button"
        class="agent-reply-draft__bar"
        :aria-expanded="false"
        @click="toggle(action.id)"
      >
        <Icon icon="mdi:creation" class="agent-reply-draft__bar-icon" />
        <span class="agent-reply-draft__bar-title">AI suggested reply</span>
        <span class="agent-reply-draft__bar-preview">{{ previewOf(action) }}</span>
        <span class="agent-reply-draft__bar-action">
          Review
          <Icon icon="mdi:chevron-up" />
        </span>
      </button>

      <div v-else class="agent-reply-draft__expanded">
        <button
          type="button"
          class="agent-reply-draft__hide"
          :aria-expanded="true"
          @click="toggle(action.id)"
        >
          Hide suggestion
          <Icon icon="mdi:chevron-down" />
        </button>
        <AgentActionCard
          :action="action"
          :busy="busyIds.has(action.id)"
          :now="now"
          title="AI suggested reply"
          approve-label="Send"
          reject-label="Dismiss"
          quick-reject
          @approve="(payload) => approve(action, payload)"
          @reject="(reason) => dismiss(action, reason ?? DISMISSED)"
        >
          <template #actions="{ busy }">
            <Button
              v-if="showOpen && replyToEmailIdOf(action)"
              variant="outline"
              size="sm"
              :disabled="busy"
              @click="emit('open', replyToEmailIdOf(action)!)"
            >
              Open email
            </Button>
            <!-- The thread list has no reply box: offering "Use" there would only dismiss the draft. -->
            <Button
              v-else-if="!showOpen"
              variant="outline"
              size="sm"
              :disabled="busy"
              @click="useInReplyBox(action)"
            >
              Use in reply box
            </Button>
          </template>
        </AgentActionCard>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Socket } from "socket.io-client";
import { Button, Icon } from "@/components/ui";
import AgentActionCard from "@/components/agent/AgentActionCard.vue";
import AgentService from "@/services/ai/agent";
import { useAgentActions } from "@/composables/agent/useAgentActions";
import { AGENT_UNREAD_REPLY_DRAFTS_KEY } from "@/composables/agent/useReplyDraftInbox";
import { getSocket } from "@/utils/socket";
import type { AgentAction, AgentReplyDraftEvent } from "@/dtos/Agent.dto";

// The AI's suggested answer to a contact's latest email or LinkedIn message, shown in the
// conversation. The rep sends it as is, edits it, takes it into the reply box to rewrite,
// or dismisses it. Drafts are written in the background when a message arrives
// (reply-drafts.ts on the server) and announced over the socket.

const props = defineProps<{
  contactId?: string | null;
  channel: "email" | "linkedin";
  /** Email only: show just the drafts that answer one of these emails (the open thread's). */
  emailIds?: string[];
  /** Offer "Open email" instead of "Use in reply box", for the thread list. */
  showOpen?: boolean;
}>();

const emit = defineEmits<{
  /** The draft, moved to the reply box: `text` is HTML for email, plain text for LinkedIn. */
  use: [draft: { text: string; subject?: string; replyToEmailId?: string }];
  /** Open the email thread the draft answers. */
  open: [emailId: string];
}>();

const REPLY_DRAFT_EVENT = "agent:reply-draft";
const DISMISSED = "Dismissed";
const MOVED_TO_REPLY_BOX = "Moved to the reply box";

const queryClient = useQueryClient();
const queryKey = computed(() => ["agent-reply-drafts", props.contactId]);

const { data } = useQuery({
  queryKey,
  queryFn: async (): Promise<AgentAction[]> => {
    const result = await AgentService.listActions({
      status: "pending",
      contactId: props.contactId!,
      source: "reply",
      limit: 10,
    });
    return result.data?.data ?? [];
  },
  enabled: computed(() => !!props.contactId),
});

const replyToEmailIdOf = (action: AgentAction): string | undefined =>
  (action.editedPayload ?? action.payload)?.replyToEmailId;

const drafts = computed(() =>
  (data.value ?? []).filter((action) => {
    if (props.channel === "linkedin") return action.actionType === "send_linkedin_message";
    if (action.actionType !== "send_email") return false;
    if (!props.emailIds) return true;
    const replyTo = replyToEmailIdOf(action);
    return !!replyTo && props.emailIds.includes(replyTo);
  })
);

// Drafts open collapsed so the conversation stays in view; the rep expands one to review it.
const expandedIds = ref(new Set<string>());
const toggle = (id: string) => {
  const next = new Set(expandedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIds.value = next;
};

/** The draft's text on one line, for the collapsed bar (email bodies are HTML). */
const previewOf = (action: AgentAction): string => {
  const input = action.editedPayload ?? action.payload ?? {};
  const raw: string = (action.actionType === "send_email" ? input.body : input.text) ?? "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// Keeps the card's status in step with each decision: "Sent", "Failed"…
const updateAction = (action: AgentAction) => {
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.map((a) => (a.id === action.id ? { ...action, contact: action.contact ?? a.contact } : a))
  );
  // Handled here, so the assistant bubble stops announcing it.
  if (action.status !== "pending") {
    queryClient.invalidateQueries({ queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY });
  }
};
const { busyIds, approve, reject } = useAgentActions(updateAction);

/** Rejects the draft and takes its card away; nothing more to show for it. */
const dismiss = async (action: AgentAction, reason: string) => {
  if (!(await reject(action, reason))) return;
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.filter((a) => a.id !== action.id)
  );
};

const useInReplyBox = (action: AgentAction) => {
  const input = action.editedPayload ?? action.payload;
  emit("use", {
    text: action.actionType === "send_email" ? input.body : input.text,
    subject: input.subject,
    replyToEmailId: input.replyToEmailId,
  });
  // The rep owns the text now; the draft itself is done with.
  return dismiss(action, MOVED_TO_REPLY_BOX);
};

// Ticks so a draft left open shows when it expires.
const now = ref(Date.now());
const timer = window.setInterval(() => (now.value = Date.now()), 30_000);

const onDraft = (event: AgentReplyDraftEvent) => {
  if (event?.contactId === props.contactId) {
    queryClient.invalidateQueries({ queryKey: queryKey.value });
  }
};

// Live updates are a nicety: drafts still load with the conversation without the socket.
let socket: Socket | null = null;
try {
  socket = getSocket();
  socket.on(REPLY_DRAFT_EVENT, onDraft);
} catch (err) {
  console.error("AI reply draft updates are unavailable:", err);
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
  socket?.off(REPLY_DRAFT_EVENT, onDraft);
});
</script>

<style scoped>
.agent-reply-draft {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  margin: 8px 0;
}

/* Collapsed: a single line that leaves the conversation its room. */
.agent-reply-draft__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #c4b5fd;
  border-radius: 10px;
  background: #f5f3ff;
  color: #111827;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.agent-reply-draft__bar:hover {
  background: #ede9fe;
}

.agent-reply-draft__bar:focus-visible,
.agent-reply-draft__hide:focus-visible {
  outline: 2px solid #7c3aed;
  outline-offset: 2px;
}

.agent-reply-draft__bar-icon {
  flex-shrink: 0;
  color: #7c3aed;
  font-size: 16px;
}

.agent-reply-draft__bar-title {
  flex-shrink: 0;
  font-weight: 600;
}

.agent-reply-draft__bar-preview {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #6b7280;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-reply-draft__bar-action {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
  color: #7c3aed;
  font-weight: 600;
}

/* Expanded: capped and scrollable, so the conversation above still shows. */
.agent-reply-draft__expanded {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 30vh;
  overflow-y: auto;
}

.agent-reply-draft__hide {
  display: inline-flex;
  align-self: flex-end;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  border: none;
  background: none;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
</style>
